import React from 'react';
import { useDispatch } from 'react-redux';
import { Row, Col, CardGroup, Card, CardBody } from 'reactstrap';
import ReactTable from 'react-table';
import { FormattedMessage, useIntl } from 'react-intl';
import { get, debounce } from 'lodash-es';
import { Query, useQuery } from 'react-apollo';
import { GetAuditLogsQuery } from '../../operations/queries/GetAuditLogs';
import { handleRequestError } from '../../constants/errorCodes';
import { formatDateTime } from '../../constants/dateFormatting';
import {
  convertIdValueArrayToObject,
  convertKeyValueArrayToObject,
  haveArraysDiverged,
} from '../../utils/arrayOperations';
import { userAuditLogTypes, adminAuditLogTypes } from '../../constants/auditLogTypes';
import { USER, HOK_ROLES } from '../../constants/roles';
import { GetEmployeesForAuditFilterQuery } from '../../operations/queries/GetUsersQuery';

export const AdminEditLogsData = ({ user, isEmployee }) => {
  const DEFAULT_PAGE_SIZE = 14;
  const { id, role } = user;
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();
  const { data, loading: employeeLoading } = useQuery(GetEmployeesForAuditFilterQuery, {
    onError: error => handleRequestError(error, dispatch),
  });

  const employeesList = get(data, 'getUsers.users', []);

  const auditLogTypes = isEmployee ? adminAuditLogTypes : userAuditLogTypes;

  return (
    <CardGroup>
      <Card className="hok-card">
        <CardBody>
          <Row className="my-4">
            <Col xs="12">
              <Query
                query={GetAuditLogsQuery}
                variables={{
                  filter: { userIdInQuestion: `${id}` },
                  order: { createdAt: 'DESC' },
                  take: DEFAULT_PAGE_SIZE,
                }}
                onError={error => handleRequestError(error, dispatch)}>
                {({ data, loading, refetch }) => {
                  const itemCount = get(data, 'getAuditLogs.queryCount', 0);
                  let items = get(data, 'getAuditLogs.audits', []);

                  const debouncedGetUsers = debounce(refetch, 400);
                  let reactTableFiltered = null;

                  const columns = [
                    {
                      headerClassName: 'react-table-headers',
                      className: 'react-table-cells',
                      Header: <FormattedMessage id="adminEditUserPage.logsTab.source" />,
                      accessor: 'who',
                      Cell: rowInfo => {
                        const who = rowInfo.original.whoUserData;
                        return who.id === id
                          ? [USER, null].includes(rowInfo.original.initiatedByRole)
                            ? formatMessage({ id: 'adminEditUserPage.logsTab.filter.self' })
                            : formatMessage({ id: 'roles.employee' })
                          : who.firstName && who.lastName
                          ? `${who.firstName} ${who.lastName}`
                          : who.id;
                      },
                      Filter: ({ filter, onChange }) => (
                        <select
                          style={{ width: '100%' }}
                          onChange={event => onChange(event.target.value)}
                          value={filter ? filter.value : ''}>
                          <option value="">{formatMessage({ id: 'adminEditUserPage.logsTab.filter.allUsers' })}</option>
                          <option value={id}>
                            {formatMessage({
                              id: HOK_ROLES.includes(role) ? 'roles.employee' : 'adminEditUserPage.logsTab.filter.self',
                            })}
                          </option>
                          {!employeeLoading &&
                            data &&
                            employeesList.map(employee => (
                              <option key={employee.id} value={employee.id}>
                                {employee.firstName && employee.lastName
                                  ? `${employee.firstName} ${employee.lastName}`
                                  : employee.id}
                              </option>
                            ))}
                        </select>
                      ),
                    },
                    {
                      headerClassName: 'react-table-headers',
                      className: 'react-table-cells',
                      Header: <FormattedMessage id="adminEditUserPage.logsTab.timeOfChange" />,
                      accessor: 'createdAt',
                      Cell: row => formatDateTime(row.value),
                      filterable: false,
                      sortable: true,
                    },
                    {
                      headerClassName: 'react-table-headers',
                      className: 'react-table-cells',
                      minWidth: 200,
                      Header: <FormattedMessage id="adminEditUserPage.logsTab.changes" />,
                      accessor: 'type',
                      Cell: row => <FormattedMessage id={`auditLogTypes.${row.value}`} />,
                      Filter: ({ filter, onChange }) => (
                        <select
                          style={{ width: '100%' }}
                          onChange={event => onChange(event.target.value)}
                          value={filter ? filter.value : ''}>
                          <option value="">{formatMessage({ id: 'adminEditUserPage.logsTab.filter.all' })}</option>
                          {auditLogTypes
                            .sort((a, b) =>
                              formatMessage({ id: `auditLogTypes.${a}` }) > formatMessage({ id: `auditLogTypes.${b}` })
                                ? 1
                                : formatMessage({ id: `auditLogTypes.${b}` }) >
                                  formatMessage({ id: `auditLogTypes.${a}` })
                                ? -1
                                : 0
                            )
                            .map(type => (
                              <option key={type} value={type}>
                                {formatMessage({ id: `auditLogTypes.${type}` })}
                              </option>
                            ))}
                        </select>
                      ),
                    },
                  ];

                  const onFetchData = state => {
                    if (data) {
                      const filtersObject = convertIdValueArrayToObject(state.filtered);
                      const order = convertKeyValueArrayToObject(
                        state.sorted.map(filter => ({ [filter.id]: filter.desc ? 'DESC' : 'ASC' }))
                      );
                      if (
                        !reactTableFiltered ||
                        !haveArraysDiverged(state.filtered, reactTableFiltered, ['who', 'createdAt', 'type'])
                      ) {
                        reactTableFiltered = state.filtered;
                        debouncedGetUsers.cancel();

                        refetch({
                          filter: {
                            ...filtersObject,
                            userIdInQuestion: `${id}`,
                          },
                          order,
                          skip: state.page * state.pageSize,
                          take: state.pageSize,
                        });
                      } else {
                        debouncedGetUsers({
                          filter: {
                            ...filtersObject,
                            userIdInQuestion: `${id}`,
                          },
                          order,
                          skip: state.page * state.pageSize,
                          take: state.pageSize,
                        });
                      }
                    }
                  };

                  return (
                    <ReactTable
                      className="-striped -highlight hok-audit-logs-table"
                      style={{ cursor: 'pointer' }}
                      filterable
                      sortable={false}
                      data={items}
                      columns={columns}
                      defaultPageSize={DEFAULT_PAGE_SIZE}
                      showPageSizeOptions={false}
                      loading={loading}
                      manual
                      onFetchData={onFetchData}
                      pages={Math.ceil(itemCount / DEFAULT_PAGE_SIZE)}
                      loadingText={<FormattedMessage id="adminUsersListPage.tableLoading" />}
                      noDataText={<FormattedMessage id="adminUsersListPage.tableNoData" />}
                      previousText={<FormattedMessage id="adminUsersListPage.tablePreviousText" />}
                      nextText={<FormattedMessage id="adminUsersListPage.tableNextText" />}
                      pageText={<FormattedMessage id="adminUsersListPage.tablePageText" />}
                      ofText={<FormattedMessage id="adminUsersListPage.tableOfText" />}
                      rowsText={formatMessage({ id: 'adminUsersListPage.tableRowsText' })}
                    />
                  );
                }}
              </Query>
            </Col>
          </Row>
        </CardBody>
      </Card>
    </CardGroup>
  );
};
